import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "../redux/store/store";
import {
  baseApi,
  useMyOrganizationsQuery,
  useSwitchOrganizationMutation,
} from "../redux/api";
import type { MyOrganization } from "../redux/api";
import { setTokens } from "../redux/slice/authSlice";
import { enterOrganization, navigate } from "../redux/slice/layoutSlice";
import type { XRole } from "../config/module-types";
import { IconBuilding, IconCheck } from "./icons";

// Card grid of the user's organizations (4 per row). Clicking a card re-issues
// tokens scoped to that org and enters it. Shared by the post-login lobby and
// the in-app "My Organizations" screen.
export const OrganizationGrid = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgSelected = useSelector((s: RootState) => s.layout.orgSelected);
  const currentOrgId = useSelector(
    (s: RootState) => s.layout.user.organizationId,
  );
  // Only mark a "current" org once one has actually been entered this session.
  const activeOrgId = orgSelected ? currentOrgId : undefined;

  // Which organization the tokens currently in hand were minted for, and
  // whether there are any. Kept accurate by setTokens (see authSlice).
  const tokenOrgId = useSelector(
    (s: RootState) => s.auth.organizationId,
  );
  const hasTokens = useSelector(
    (s: RootState) => !!s.auth.accessToken,
  );
  const sessionRole = useSelector(
    (s: RootState) => s.layout.user.role,
  );

  const {
    data: orgs = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useMyOrganizationsQuery();
  const [switchOrg] = useSwitchOrganizationMutation();

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const enter = async (org: MyOrganization) => {
    if (busyId) return;
    setActionError(null);

    // Nothing to fetch when the tokens in hand already address this
    // organization. Two ways to land here: clicking the org you are already
    // in, and a single-organization user's first sign-in — `login` already
    // mints tokens against their first organization (see createLoginTokens),
    // so the switch that followed re-minted tokens for the org it was already
    // on. Every switch creates a session row server-side, so that sign-in cost
    // two and orphaned the first immediately.
    //
    // BOTH signals must agree. auth.organizationId is only populated from here
    // on, so an existing session has it empty and simply falls through to a
    // real switch; requiring activeOrgId to match too (or be absent, which is
    // only true before an org has been entered) stops a stale value entering an
    // org the tokens do not address.
    const tokensAlreadyHere =
      hasTokens &&
      !!tokenOrgId &&
      org.id === tokenOrgId &&
      (!activeOrgId || activeOrgId === org.id);

    if (tokensAlreadyHere) {
      // No resetApiState: the cache belongs to this same org, so there is
      // nothing cross-tenant to drop and a full refetch would be pure cost.
      dispatch(
        enterOrganization({
          organizationId: org.id,
          organizationName: org.name,
          // Mirrors the switch path's precedence, with the session role
          // standing in for the tokens we did not fetch.
          role: ((sessionRole as XRole) || (org.role as XRole)) as XRole,
        }),
      );
      // Same redirect the switch path does, read from the list rather than
      // from tokens we deliberately did not fetch.
      if (org.organizationStatus === "expired") {
        dispatch(navigate("/subscriptions"));
      }
      return;
    }

    setBusyId(org.id);
    try {
      const tokens = await switchOrg(org.id).unwrap();
      dispatch(
        setTokens({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          sessionToken: tokens.sessionToken,
          // Keeps auth.organizationId honest about what these tokens address,
          // which is what the skip above reads.
          organizationId: org.id,
        }),
      );
      // Drop any data cached under the previous org before entering the new one.
      dispatch(baseApi.util.resetApiState());
      dispatch(
        enterOrganization({
          organizationId: org.id,
          organizationName: org.name,
          role: ((tokens.role as XRole) || (org.role as XRole)) as XRole,
        }),
      );
      // An expired org only lets its owner through (server-enforced) — send
      // them straight to renew instead of their normal module home. Routing
      // matches against the route's `path`, not its screen key — the Owner
      // module's "my-subscription" screen lives at /subscriptions.
      if (tokens.organizationStatus === "expired") {
        dispatch(navigate("/subscriptions"));
      }
    } catch (err) {
      setActionError(
        (err as { message?: string })?.message ??
          "Could not enter organization.",
      );
      setBusyId(null);
    }
  };

  if (isLoading) {
    return <p className="org-grid__msg">Loading your organizations…</p>;
  }

  if (error) {
    return (
      <div className="org-grid__msg">
        <p>
          {(error as { message?: string }).message ??
            "Failed to load your organizations."}
        </p>
        <button type="button" className="btn" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  }

  if (orgs.length === 0) {
    return (
      <p className="org-grid__msg">
        You don't belong to any organization yet.
      </p>
    );
  }

  return (
    <>
      {actionError ? <div className="form-error">{actionError}</div> : null}
      <div className="org-grid">
        {orgs.map((org) => {
          const isActive = org.id === activeOrgId;
          const inactive = (org.status ?? "").toLowerCase() !== "active";
          const isOwner = (org.role ?? "").toLowerCase() === "owner";
          const expired = (org.organizationStatus ?? "").toLowerCase() === "expired";
          // Everyone except the org's own owner is locked out of an expired
          // org — the owner can still click through to renew it.
          const blocked = expired && !isOwner;
          return (
            <button
              key={org.id}
              type="button"
              className={`org-card${isActive ? " org-card--active" : ""}${expired ? " org-card--expired" : ""}`}
              onClick={() => enter(org)}
              disabled={!!busyId || inactive || blocked}
              title={
                blocked
                  ? "This organization's subscription has expired. Ask the owner to renew it."
                  : inactive
                    ? `Membership ${org.status}`
                    : undefined
              }
            >
              <span className="org-card__icon">
                <IconBuilding size={22} />
              </span>
              <span className="org-card__name">{org.name}</span>
              <span className="org-card__role">{org.role}</span>
              <span className="org-card__cta">
                {busyId === org.id ? (
                  "Entering…"
                ) : isActive ? (
                  <>
                    <IconCheck size={14} /> Current
                  </>
                ) : inactive ? (
                  org.status
                ) : blocked ? (
                  "Subscription expired"
                ) : expired ? (
                  "Renew subscription →"
                ) : (
                  "Enter →"
                )}
              </span>
            </button>
          );
        })}
      </div>
      {isFetching ? <p className="org-grid__hint">Refreshing…</p> : null}
    </>
  );
};

export default OrganizationGrid;
